import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Picto } from '../../ui/pictos.tsx'

/** The workshop pages' shared pieces (M23-F). */

/** Closes a form the moment its action was kept on the device (M23 item 10): `useWorkshopAction` answers `pending` instead of an error when the
 *  network is away; the caller passes what closes its form, and shows `PendingBanner` where the form was. Runs once per pending verdict. */
export function useCloseOnPending(pending: boolean, close: () => void): void {
  const fn = useRef(close)
  fn.current = close
  useEffect(() => { if (pending) fn.current() }, [pending])
}

/** A destructive act in two taps (M23 item 36: a part line, a mount and a vendor id were removed in one tap): the first tap turns the button into its
 *  confirmation beside an Annuler, the second acts. The first button keeps its test id, the confirmation adds `-confirm`. */
export function TwoTap({ label, confirm, onConfirm, disabled, danger, testId, className = '' }: {
  label: ReactNode; confirm: ReactNode; onConfirm: () => void; disabled?: boolean; danger?: boolean; testId?: string; className?: string
}) {
  const { t } = useTranslation()
  const [sure, setSure] = useState(false)
  if (!sure) return <button type="button" className={`btn ${className}`} disabled={disabled} onClick={() => setSure(true)} data-testid={testId}>{label}</button>
  return (
    <span className="flex flex-wrap gap-2">
      <button type="button" className={`btn ${danger ? 'btn-danger' : 'btn-primary'} ${className}`} disabled={disabled} onClick={() => { setSure(false); onConfirm() }} data-testid={testId ? `${testId}-confirm` : undefined}>{confirm}</button>
      <button type="button" className="btn" onClick={() => setSure(false)}>{t('common.cancel')}</button>
    </span>
  )
}

/** A section folded under a 56-px summary (M23 item 22: the office panels of a machine's page open only when they hold rows; the jardinier's
 *  "Pour l'atelier"). A card; the chevron turns when open. */
export function Fold({ title, help, open, children, testId }: { title: ReactNode; help?: string; open?: boolean; children: ReactNode; testId?: string }) {
  // the contents exist only while the fold is open: a folded form must not duplicate the ids and test ids of a form shown elsewhere on the page
  // (the fill-up's meter reading beside the meter panel's own, found by meters.spec after M15-A)
  const [shown, setShown] = useState(!!open)
  return (
    <details className="group card" open={open} data-testid={testId} onToggle={(e) => setShown((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-3 text-xl font-bold [&::-webkit-details-marker]:hidden" data-testid={testId ? `${testId}-toggle` : undefined}>
        <span className="min-w-0">{title}</span>
        <Picto name="chevron" className="size-5 shrink-0 rotate-90 opacity-60 transition-transform group-open:-rotate-90" strokeWidth={2.2} />
      </summary>
      {shown && (
        <div className="mt-2 flex flex-col gap-3">
          {help && <p className="meta">{help}</p>}
          {children}
        </div>
      )}
    </details>
  )
}

/** The summary of a `<details>` inside a card, 56 px (M23 item 22: MeterPanel's 26-px summaries), with the chevron. */
export function FoldSummary({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-3 text-base font-semibold [&::-webkit-details-marker]:hidden" data-testid={testId}>
      <span className="min-w-0">{children}</span>
      <Picto name="chevron" className="size-5 shrink-0 rotate-90 opacity-60 transition-transform group-open:-rotate-90" strokeWidth={2.2} />
    </summary>
  )
}
